/**
 * Calling provider (ADR-046): whether the callers' calls go through Twilio,
 * billed by the started minute, or through our own Asterisk and its SIP
 * trunk, billed by the second. Asterisk is driven by the framework's
 * swoft-telephony service: Revcall needs its address, its API key and the
 * secret its events are signed with. Stored in AdminSettings under
 * `calling_provider` through GET/PUT /api/admin/settings/calling_provider,
 * which masks the key and the secret. A pilot list keeps everyone else on
 * Twilio.
 *
 * W6 (B3-AdminCallingProvider):
 *   - who is touched comes from GET /api/admin/platform/calling-provider-overview
 *     (members who call, per organisation, and 30 days of call durations);
 *   - a change of provider or of pilots goes through a ConfirmDialog that says
 *     who moves to which carrier, in how many organisations; credentials alone
 *     save directly;
 *   - pilots are picked among the members (search, checkboxes, pills), no more
 *     free text; a pilot that matches nobody is flagged;
 *   - a cost box: the same 30 days billed by the started minute and by the second.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PlugZap, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, ApiErrorException } from '@/api/client';
import { getCallingProviderOverview } from '@/api/endpoints/platform';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { VFStatCard } from '@/components/vf/StatCard';
import { Checkbox, ErrorLine, PlatformPageHeader, RadioGroup, RadioGroupItem } from '@/components/backoffice/platform/PlatformBits';
import { perSecondSaving, providerImpact, routingChanged, type Member } from '@/components/backoffice/platform/callingProviderImpact';
import { currentLocale } from '@/i18n/locale';

type Provider = 'twilio' | 'asterisk';

interface CallingProviderValue {
  provider: Provider;
  telephonyUrl: string;
  telephonyApiKey: string;
  telephonyWebhookSecret: string;
  /** Callers on Asterisk; empty means every caller. */
  pilotEmails: string[];
}

interface TestResult { ok: boolean; version?: string; error?: string }

const EMPTY: CallingProviderValue = { provider: 'twilio', telephonyUrl: '', telephonyApiKey: '', telephonyWebhookSecret: '', pilotEmails: [] };
const KEY = 'calling_provider';
const FIELDS: Array<{ name: Exclude<keyof CallingProviderValue, 'provider' | 'pilotEmails'>; secret?: boolean; placeholder: string }> = [
  { name: 'telephonyUrl', placeholder: 'http://127.0.0.1:8032' },
  { name: 'telephonyApiKey', secret: true, placeholder: '' },
  { name: 'telephonyWebhookSecret', secret: true, placeholder: '' },
];

/** Where swoft-telephony posts its events (its TEL_WEBHOOK_URL). */
function webhookUrl(): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
  const absolute = /^https?:\/\//.test(base) ? base : `${window.location.origin}${base}`;
  return `${absolute.replace(/\/+$/, '')}/telephony/events`;
}

async function readSetting(): Promise<CallingProviderValue> {
  try {
    const s = await apiClient.get<{ value?: Partial<CallingProviderValue> }>(`/admin/settings/${KEY}`);
    const value = { ...EMPTY, ...(s.value ?? {}) };
    return { ...value, pilotEmails: Array.isArray(value.pilotEmails) ? value.pilotEmails : [] };
  } catch (err) {
    // No setting yet: Twilio for everyone, the server's default.
    if (err instanceof ApiErrorException && err.status === 404) return EMPTY;
    throw err;
  }
}

function PilotPicker({ members, value, onChange }: { members: Member[]; value: string[]; onChange: (next: string[]) => void }) {
  const { t } = useTranslation(['admin']);
  const [query, setQuery] = useState('');
  const selected = new Set(value);
  const known = new Set(members.map((m) => m.email));
  const q = query.trim().toLowerCase();
  const shown = q
    ? members.filter((m) => m.email.includes(q) || m.full_name.toLowerCase().includes(q) || m.organisation_name.toLowerCase().includes(q))
    : members;
  const toggle = (email: string) => onChange(selected.has(email) ? value.filter((e) => e !== email) : [...value, email]);
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{t('admin:callingProvider.pilotEmails')}</legend>
      <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:callingProvider.pilotEmailsHint')}</p>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={t('admin:callingProvider.pilotsSelected')}>
          {value.map((email) => (
            <li key={email} className="inline-flex h-8 items-center gap-1 rounded-full border pl-3 text-[12px]" style={{ borderColor: known.has(email) ? 'var(--vf-accent-bd)' : 'var(--vf-orange)', background: known.has(email) ? 'var(--vf-accent-bg)' : 'var(--vf-orange-bg)' }}>
              {email}
              {!known.has(email) && <span className="ml-1" style={{ color: 'var(--vf-text-2)' }}>{t('admin:callingProvider.pilotUnknown')}</span>}
              <button type="button" onClick={() => toggle(email)} aria-label={t('admin:callingProvider.removePilot', { email })} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-[var(--vf-surface-3)]">
                <X className="h-3 w-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('admin:callingProvider.pilotSearch')} aria-label={t('admin:callingProvider.pilotSearch')} />
      <div className="max-h-64 overflow-y-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
        {shown.length === 0 ? (
          <p className="px-3 py-4 text-sm" style={{ color: 'var(--vf-text-2)' }}>{members.length === 0 ? t('admin:callingProvider.noMembers') : t('admin:callingProvider.noPilotMatch')}</p>
        ) : (
          <ul>
            {shown.map((m) => {
              const id = ['pilot', m.organisation_id, m.email].join('-');
              return (
                <li key={`${m.organisation_id}-${m.email}`} className="flex min-h-11 items-center gap-3 border-t px-3 first:border-t-0" style={{ borderColor: 'var(--vf-border)' }}>
                  <Checkbox id={id} checked={selected.has(m.email)} onCheckedChange={() => toggle(m.email)} />
                  <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer py-2 text-sm">
                    <span className="block truncate">{m.full_name && m.full_name !== m.email ? `${m.full_name} · ${m.email}` : m.email}</span>
                    <span className="block truncate text-xs" style={{ color: 'var(--vf-text-2)' }}>{m.organisation_name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </fieldset>
  );
}

export default function AdminCallingProvider() {
  const { t } = useTranslation(['admin', 'common', 'settings']);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CallingProviderValue>(EMPTY);
  const [test, setTest] = useState<TestResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  const setting = useQuery({ queryKey: ['admin-setting', KEY], queryFn: readSetting });
  const overview = useQuery({ queryKey: ['calling-provider-overview'], queryFn: ({ signal }) => getCallingProviderOverview(signal) });
  useEffect(() => {
    if (setting.data) setForm(setting.data);
  }, [setting.data]);

  const members = useMemo(() => overview.data?.members ?? [], [overview.data]);
  const impact = useMemo(() => providerImpact(form.provider, form.pilotEmails, members), [form.provider, form.pilotEmails, members]);

  const save = useMutation({
    mutationFn: (value: CallingProviderValue) => apiClient.put(`/admin/settings/${KEY}`, { value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-setting', KEY] });
      void queryClient.invalidateQueries({ queryKey: ['calling-provider-overview'] });
      setTest(null);
      setConfirming(false);
      toast.success(t('admin:callingProvider.saved'));
    },
  });
  const runTest = useMutation({
    mutationFn: () => apiClient.post<TestResult>('/admin/calling-provider/test', {}),
    onSuccess: (r) => setTest(r),
    onError: (e) => setTest({ ok: false, error: (e as Error).message }),
  });

  const asterisk = form.provider === 'asterisk';
  const incomplete = asterisk && FIELDS.some((f) => !form[f.name].trim());
  const needsConfirmation = routingChanged(setting.data ?? EMPTY, form);
  const onSave = () => (needsConfirmation ? setConfirming(true) : save.mutate(form));

  if (setting.isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" style={{ color: 'var(--vf-accent)' }} aria-label={t('common:actions.loading')} />
      </div>
    );
  }

  const usage = overview.data?.usage;
  const saving = usage ? perSecondSaving(usage) : null;
  const fmt = (n: number) => n.toLocaleString(currentLocale(), { maximumFractionDigits: 1 });
  const inForce = overview.data?.provider;

  const impactText = impact.provider === 'twilio'
    ? t('admin:callingProvider.confirm.toTwilio', { count: impact.twilio.length, organisations: impact.organisations })
    : impact.twilio.length === 0
      ? t('admin:callingProvider.confirm.toAsteriskAll', { count: impact.asterisk.length, organisations: impact.asteriskOrganisations })
      : t('admin:callingProvider.confirm.toAsteriskPilots', { count: impact.asterisk.length, organisations: impact.asteriskOrganisations, others: impact.twilio.length });

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <PlatformPageHeader title={t('admin:superAdminLayout.nav.callingProvider')} subtitle={t('admin:callingProvider.subtitle')} />

      {setting.isError && <ErrorLine message={t('admin:callingProvider.loadError')} error={setting.error} onRetry={() => void setting.refetch()} />}

      <section aria-labelledby="cp-status" className="space-y-3 rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
        <h2 id="cp-status" className="text-base font-semibold">{t('admin:callingProvider.status.title')}</h2>
        {overview.isError && <ErrorLine message={t('admin:callingProvider.status.loadError')} error={overview.error} onRetry={() => void overview.refetch()} />}
        {inForce && (
          <p className="text-sm">
            {t('admin:callingProvider.status.inForce', { provider: t(`admin:callingProvider.providers.${inForce}`) })}
            {overview.data?.fallback_reason && <> {t('admin:callingProvider.status.fallback', { reason: overview.data.fallback_reason })}</>}
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <VFStatCard
            label={t('admin:callingProvider.cost.startedMinute')}
            state={overview.isError ? 'error' : usage ? 'ready' : 'loading'}
            value={usage ? (usage.calls === 0 ? '—' : t('admin:callingProvider.cost.minutes', { value: fmt(usage.started_minute) })) : undefined}
            hint={t('admin:callingProvider.cost.hint', { days: usage?.period_days ?? 30, count: usage?.calls ?? 0 })}
          />
          <VFStatCard
            label={t('admin:callingProvider.cost.perSecond')}
            state={overview.isError ? 'error' : usage ? 'ready' : 'loading'}
            value={usage ? (usage.calls === 0 ? '—' : t('admin:callingProvider.cost.minutes', { value: fmt(usage.per_second) })) : undefined}
            hint={t('admin:callingProvider.cost.hint', { days: usage?.period_days ?? 30, count: usage?.calls ?? 0 })}
          />
          <VFStatCard
            label={t('admin:callingProvider.cost.saving')}
            state={overview.isError ? 'error' : usage ? 'ready' : 'loading'}
            value={saving ? (saving.share === null ? '—' : t('admin:callingProvider.cost.savingValue', { value: fmt(saving.minutes), share: Math.round(saving.share * 100) })) : undefined}
          />
        </div>
        <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:callingProvider.cost.note', { days: usage?.period_days ?? 30, count: usage?.calls ?? 0 })}</p>
      </section>

      <div className="space-y-5 rounded-vf border p-4 sm:p-6" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
        <fieldset className="space-y-3">
          <legend className="font-medium">{t('admin:callingProvider.label')}</legend>
          <RadioGroup value={form.provider} onValueChange={(provider) => setForm({ ...form, provider: provider as Provider })} className="gap-3">
            {(['twilio', 'asterisk'] as const).map((p) => {
              const selectedP = form.provider === p;
              return (
                <label key={p} htmlFor={`cp-${p}`} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-vf border p-3" style={{ borderColor: selectedP ? 'var(--vf-accent)' : 'var(--vf-border)', background: selectedP ? 'var(--vf-accent-bg)' : undefined }}>
                  <RadioGroupItem value={p} id={`cp-${p}`} className="mt-0.5" />
                  <span className="space-y-1">
                    <span className="block font-medium">{t(`admin:callingProvider.providers.${p}`)}</span>
                    <span className="block text-xs" style={{ color: 'var(--vf-text-2)' }}>{t(`admin:callingProvider.${p}Hint`)}</span>
                  </span>
                </label>
              );
            })}
          </RadioGroup>
        </fieldset>

        {asterisk && (
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.name} className="space-y-2">
                <Label htmlFor={`cp-${f.name}`}>{t(`admin:callingProvider.${f.name}`)}</Label>
                <Input
                  id={`cp-${f.name}`}
                  name={`cp-setting-${f.name}`}
                  type={f.secret ? 'password' : 'text'}
                  // REV-W6-F6: « off » does not stop a browser from filling a
                  // password field with the admin's login password.
                  autoComplete={f.secret ? 'new-password' : 'off'}
                  placeholder={f.placeholder}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                />
                {f.name === 'telephonyWebhookSecret' && <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:callingProvider.telephonyWebhookHint', { url: webhookUrl() })}</p>}
              </div>
            ))}
          </div>
        )}
        {asterisk && <PilotPicker members={members} value={form.pilotEmails} onChange={(pilotEmails) => setForm({ ...form, pilotEmails })} />}
        {incomplete && <p className="text-xs" style={{ color: 'var(--vf-text-1)' }}>{t('admin:callingProvider.incomplete')}</p>}

        <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:callingProvider.scope')}</p>

        {save.isError && <ErrorLine message={t('admin:callingProvider.saveError')} error={save.error} />}
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={onSave} disabled={save.isPending || setting.isError}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" /> : <Save className="mr-2 h-4 w-4" />}
            {save.isPending ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
          {asterisk && (
            <Button variant="outline" onClick={() => runTest.mutate()} disabled={runTest.isPending || incomplete}>
              {runTest.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" /> : <PlugZap className="mr-2 h-4 w-4" />}
              {t('admin:callingProvider.test')}
            </Button>
          )}
          {test && (
            <span role="status" className="text-sm" style={{ color: test.ok ? 'var(--vf-fill-success)' : 'var(--vf-fill-danger)' }}>
              {test.ok ? t('admin:callingProvider.testOk', { version: test.version }) : t('admin:callingProvider.testFail', { error: test.error })}
            </span>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        tone="default"
        title={t('admin:callingProvider.confirm.title', { provider: t(`admin:callingProvider.providers.${form.provider}`) })}
        description={
          <span className="block space-y-2">
            <span className="block">{impactText}</span>
            {impact.provider === 'asterisk' && impact.asterisk.length > 0 && impact.twilio.length > 0 && (
              <span className="block text-xs">{t('admin:callingProvider.confirm.pilots', { list: impact.asterisk.map((m) => m.email).join(', ') })}</span>
            )}
            {impact.unknownPilots.length > 0 && (
              <span className="block text-xs">{t('admin:callingProvider.confirm.unknownPilots', { list: impact.unknownPilots.join(', ') })}</span>
            )}
            {incomplete && <span className="block text-xs">{t('admin:callingProvider.incomplete')}</span>}
            <span className="block text-xs">{t('admin:callingProvider.scope')}</span>
          </span>
        }
        confirmLabel={t('admin:callingProvider.confirm.action')}
        busy={save.isPending}
        onConfirm={() => save.mutateAsync(form).then(() => undefined)}
      />
    </div>
  );
}
